import { HTMLAttributes, useCallback } from 'react'
import { lowerCase } from 'lodash'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import LanguageList from '@/components/layout/language-list'
import { useStore } from '@/contexts/store'
import { useLocaleStore } from '@/store/locale'
import { stripSlash } from '@/utils/path'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLDivElement>

const Footer = ({ className, ...props }: FooterProps) => {
  const { strings, menu } = useStore()
  const router = useRouter()
  const currentLocale = useLocaleStore((state) => state.currentLocale)

  const isActive = useCallback(
    (url: string) =>
      stripSlash(url) ===
      stripSlash(
        `${currentLocale !== router.defaultLocale ? `/${currentLocale}` : ''}${router.asPath}`
      ),
    [router, currentLocale]
  )

  return (
    <>
      <div
        className={cn(
          'dark relative',
          'bg-section-backgroud-light dark:bg-section-backgroud-dark'
        )}
      >
        <Container>
          <Grid>
            <div className="col-span-4 md:col-span-6 lg:col-span-12">
              <hr className="border-[1px] border-dashed border-surface-primay-invert-dark opacity-30 dark:border-surface-primay-invert-light" />
            </div>
          </Grid>
        </Container>
      </div>
      <footer
        className={cn(
          'dark relative',
          'bg-section-backgroud-light dark:bg-section-backgroud-dark',
          'pt-s-spacer-md md:pt-d-spacer-md',
          'pb-s-spacer-lg md:pb-d-spacer-lg',
          className
        )}
        {...props}
      >
        <Container className="grid gap-y-s-spacer-md md:gap-y-d-spacer-md">
          <Grid className="gap-y-spacing-lg">
            <div
              className={cn(
                'col-span-2 md:order-3 md:col-span-1 lg:col-span-2',
                'md:flex md:justify-end'
              )}
            >
              {strings?.link_corporate?.url ? (
                <a
                  href={strings.link_corporate.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex"
                >
                  <span className="sr-only">
                    {strings.link_corporate.title}
                  </span>
                  <SVGIcon
                    symbol="logo-corporate"
                    className="h-[5rem] w-[10.1rem] fill-surface-logo-inverted-dark dark:fill-surface-logo-inverted-light"
                  />
                </a>
              ) : (
                <Link href="/" scroll={false} className="flex">
                  <span className="sr-only">{strings?.brand_name}</span>
                  <SVGIcon
                    symbol="logo-corporate"
                    className="h-[5rem] w-[10.1rem] fill-surface-logo-inverted-dark dark:fill-surface-logo-inverted-light"
                  />
                </Link>
              )}
            </div>

            <div className="col-span-2 md:order-1 md:col-span-3 lg:col-span-6">
              <nav>
                <ul className="grid gap-y-spacing-xs">
                  {menu?.header_menu_02?.menu?.map(
                    ({ url, title, target }, index) => (
                      <li key={`header-menu-02-${index}`}>
                        <Button asChild variant="primary" isLiquid isInverted>
                          <Link href={url} target={target} scroll={false}>
                            <Typography variant="h6">{title}</Typography>
                          </Link>
                        </Button>
                      </li>
                    )
                  )}
                  {menu?.footer_menu_01?.menu?.map(
                    ({ url, title, target }, index) => (
                      <li key={`footer-menu-01-${index}`} className="flex">
                        <Link
                          href={url}
                          target={target}
                          scroll={false}
                          className="inline-flex"
                        >
                          <Typography
                            variant="h6"
                            className={cn([
                              'underline-effect',
                              {
                                'underline-active pointer-events-none':
                                  isActive(url)
                              }
                            ])}
                          >
                            {title}
                          </Typography>
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              </nav>
            </div>

            <div
              className={cn(
                'col-span-2 md:order-2 md:col-span-2 lg:col-span-4',
                'flex flex-col justify-between space-y-spacing-xs md:space-y-0'
              )}
            >
              <nav>
                <ul className="grid gap-y-spacing-xs">
                  {menu?.footer_menu_02?.menu?.map(
                    ({ url, title, target }, index) => (
                      <li key={`footer-menu-02-${index}`} className="flex">
                        <Link
                          href={url}
                          target={target}
                          scroll={false}
                          className="inline-flex"
                        >
                          <Typography
                            variant="large"
                            className={cn([
                              'underline-effect',
                              {
                                'underline-active pointer-events-none':
                                  isActive(url)
                              }
                            ])}
                          >
                            {title}
                          </Typography>
                        </Link>
                      </li>
                    )
                  )}
                </ul>
              </nav>

              <div className="flex">
                <LanguageList />
              </div>
            </div>
          </Grid>

          <Grid className="items-end gap-y-spacing-md">
            <div
              className={cn(
                'col-span-2 md:col-span-4 lg:col-span-9 xl:col-span-10',
                'grid gap-y-spacing-lg md:gap-y-spacing-xs'
              )}
            >
              <ul
                className={cn(
                  'flex flex-col space-y-spacing-xs',
                  'md:flex-row md:space-x-spacing-sm md:space-y-0'
                )}
              >
                {menu?.legals_menu?.menu?.map(
                  ({ url, title, target }, index) => (
                    <li key={`legals-menu-${index}`} className="flex">
                      <Link
                        href={url}
                        target={target}
                        scroll={false}
                        className="-m-[0.5rem] inline-flex p-[0.5rem]"
                      >
                        <Typography
                          variant="regular"
                          className="underline-effect capitalize"
                        >
                          {title}
                        </Typography>
                      </Link>
                    </li>
                  )
                )}
              </ul>

              <Typography
                variant="small"
                paragraph
                className="[&_a]:underline-effect text-text-secondary-invert-light dark:text-text-secondary-invert-light"
              >
                {strings?.footer?.colophon.replace(
                  '[YEAR]',
                  new Date().getFullYear()
                )}
              </Typography>
            </div>

            <div
              className={cn(
                'col-span-2 md:col-span-2 lg:col-span-3 xl:col-span-2',
                'md:flex md:justify-end'
              )}
            >
              <ul className="flex space-x-spacing-2xs">
                {menu?.social_menu?.menu?.map(
                  ({ url, title, target }, index) => (
                    <li key={`social-menu-${index}`} className="flex">
                      <Link
                        href={url}
                        target={target}
                        scroll={false}
                        className="inline-flex h-[4rem] w-[4rem] items-center justify-center rounded-full border border-surface-secondary-invert-light transition-all duration-300 ease-cubic-default hover:bg-surface-secondary-invert-light/20"
                      >
                        <span className="sr-only">{title}</span>
                        <SVGIcon
                          symbol={`${lowerCase(title)}-icon`}
                          className="h-[1.5rem] w-[1.4rem] fill-surface-icon-light"
                        />
                      </Link>
                    </li>
                  )
                )}
              </ul>
            </div>
          </Grid>
        </Container>
      </footer>
    </>
  )
}

export default Footer
